import { httpsCallable } from 'firebase/functions';
import { doc, onSnapshot, type Unsubscribe } from 'firebase/firestore';
import { db, functions } from '../config/firebase';

export interface CreditBalance {
  bakiye: number;
  rezervasyon: number;
  toplamKullanilan: number;
  toplamEklenen: number;
  iadeHakki: number;
}

export interface SanayiKrediCatalogItem {
  id: string;
  hakSayisi: number;
  etiket: string;
  aciklama?: string;
  priceUsd?: number;
  currency?: string;
}

export interface IadeConfig {
  aktif: boolean;
  sureSaat: number;
  beklemeSureDakika: number;
}

export interface SanayiKrediCatalogResponse {
  mode: 'test_mode' | 'live_mode';
  sanayiRaporuUcretsiz: boolean;
  paketler: SanayiKrediCatalogItem[];
  bonusDurationDays: number;
  iadeConfig: IadeConfig;
}

interface CreateSanayiKrediCheckoutRequest {
  paketId: string;
  email?: string;
  returnPath?: string;
}

export interface CreateSanayiKrediCheckoutResponse {
  checkoutUrl: string;
  sessionId: string;
  mode: 'test_mode' | 'live_mode';
  paket: {
    id: string;
    hakSayisi: number;
    etiket?: string;
  };
  product?: {
    name: string;
    description?: string;
    priceAmount: number;
    priceCurrency: string;
  };
}

function parseNumber(value: unknown): number {
  return typeof value === 'number' && Number.isFinite(value) ? value : 0;
}

export function parseCreditBalance(raw: unknown): CreditBalance {
  const value = raw && typeof raw === 'object' ? (raw as Record<string, unknown>) : {};
  return {
    bakiye: parseNumber(value.bakiye),
    rezervasyon: parseNumber(value.rezervasyon),
    toplamKullanilan: parseNumber(value.toplamKullanilan),
    toplamEklenen: parseNumber(value.toplamEklenen),
    iadeHakki: parseNumber(value.iadeHakki),
  };
}

export function subscribeCreditBalance(
  userId: string,
  onNext: (balance: CreditBalance) => void,
  onError?: (error: Error) => void
): Unsubscribe {
  const userRef = doc(db, 'users', userId);
  return onSnapshot(
    userRef,
    (snapshot) => {
      if (!snapshot.exists()) {
        onNext(parseCreditBalance(null));
        return;
      }
      onNext(parseCreditBalance(snapshot.data()?.sanayiKredi));
    },
    (error) => {
      if (onError) {
        onError(error);
      }
    }
  );
}

export async function getSanayiKrediCatalog(): Promise<SanayiKrediCatalogResponse> {
  const callable = httpsCallable<Record<string, never>, SanayiKrediCatalogResponse>(
    functions,
    'getSanayiKrediCatalog'
  );
  const result = await callable({});
  return result.data;
}

export async function createSanayiKrediCheckout(
  payload: CreateSanayiKrediCheckoutRequest
): Promise<CreateSanayiKrediCheckoutResponse> {
  const callable = httpsCallable<
    CreateSanayiKrediCheckoutRequest,
    CreateSanayiKrediCheckoutResponse
  >(functions, 'createSanayiKrediCheckout');
  const result = await callable(payload);
  return result.data;
}

// ── Rapor İade ──────────────────────────────────────

export interface SanayiRaporuIadeRequest {
  conversationId: string;
  iadeSebebi: string;
  iadeAciklama?: string;
}

export interface SanayiRaporuIadeResponse {
  success: boolean;
  beklemeSureDakika: number;
  iadeOnayTarihi: string;
}

export async function sanayiRaporuIade(
  payload: SanayiRaporuIadeRequest
): Promise<SanayiRaporuIadeResponse> {
  const callable = httpsCallable<SanayiRaporuIadeRequest, SanayiRaporuIadeResponse>(
    functions,
    'sanayiRaporuIade'
  );
  const result = await callable(payload);
  return result.data;
}
